import { createUseStoreWithSelectorDeferred } from "@captions-web/utils/zustand-store";
import { createStore } from "zustand";
import { combine } from "zustand/middleware";

import { createStoreGetters } from "~/stores/root";

export type AudioPermissionsState = PermissionState | "loading" | "unknown";

export type AudioDeviceInfo = {
  deviceId: string;
  groupId: string;
  label: string;
  default?: boolean;
};

export type AudioPermissionsStoreValue = {
  state: AudioPermissionsState;
  audioDevices: AudioDeviceInfo[];
  selectedDeviceId?: string;
  isGrantingPermissions: boolean;
};

export const createAudioPermissionsStore = () => {
  return createStore(
    combine(
      {
        state: "loading",
        audioDevices: [],
        selectedDeviceId: undefined,
        isGrantingPermissions: false,
      } as AudioPermissionsStoreValue,
      (set, get) => {
        // internal helper functions
        async function updateInputDevices() {
          const result = await getInputDevices();

          if (result !== "failed-to-list-devices") {
            const audioDevices = result;
            set({ audioDevices });

            const selectedDeviceId = get().selectedDeviceId;

            // for audio selected devices, if the device is no longer available, select the first available device
            if (!audioDevices.find((device) => device.deviceId === selectedDeviceId)) {
              set({ selectedDeviceId: audioDevices[0]?.deviceId });
            }
          }
        }

        async function updatePermissionState() {
          const state = await queryPermissionState();
          set({ state, isGrantingPermissions: false });

          if (state === "granted") {
            await updateInputDevices();
          }

          return state;
        }

        return {
          async requestPermissions() {
            set({ isGrantingPermissions: true });

            const result = await requestPermissionForUserMedia();

            if (result === "failed-to-request-permission") {
              set({ isGrantingPermissions: false, state: "denied" });

              return result;
            }

            return updatePermissionState();
          },
          selectDevice(deviceId: string) {
            set({ selectedDeviceId: deviceId });
          },
          async init() {
            updatePermissionState();
            if (typeof navigator.mediaDevices?.addEventListener === "function") {
              navigator.mediaDevices.addEventListener("devicechange", updateInputDevices);
            }
          },
          onUnmount() {
            // probably not needed since these stores are global
            if (typeof window.navigator.mediaDevices?.removeEventListener === "function") {
              navigator.mediaDevices.removeEventListener("devicechange", updateInputDevices);
            }
          },
        };
      }
    )
  );
};

type AudioPermissionsStore = ReturnType<ReturnType<typeof createAudioPermissionsStore>["getState"]>;

export const {
  getStore: getAudioCreationStore,
  getStoreAsync: getAudioCreationStoreAsync,
  getStoreApi: getAudioCreationStoreApi,
} = createStoreGetters<AudioPermissionsStore>("audio-permissions");

export const useAudioPermissionsStore = createUseStoreWithSelectorDeferred(() =>
  getAudioCreationStoreApi()
);

// helpers to interact with browser APIs

// this may fail if browser does not support permissions API
// that's ok we will just fallback to showing the permission prompt
async function queryPermissionState() {
  try {
    const result = await navigator.permissions.query({ name: "microphone" as PermissionName });

    return result.state;
  } catch (e) {
    console.error("could not query permission status", e);
    return "unknown";
  }
}

async function requestPermissionForUserMedia() {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });

    stream.getTracks().forEach((track) => {
      track.stop();
    });

    return "permission-granted" as const;
  } catch (e) {
    console.error("could not get permission for user media", e);
    return "failed-to-request-permission" as const;
  }
}

async function getInputDevices() {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioDevices = devices.filter((device) => device?.kind === "audioinput");
    const defaultDevice = findDefaultDevice(audioDevices);
    const filteredDevices: AudioDeviceInfo[] = audioDevices
      .filter((device) => !isIgnoredDevice(device))
      .filter((device) => device !== defaultDevice)
      .map((device) => ({
        label: cleanDeviceLabel(device.label),
        deviceId: device.deviceId,
        groupId: device.groupId,
        default: device.groupId === defaultDevice?.groupId,
      }))
      .sort((a, b) => {
        if (a.default) {
          return -1; // list defaults first
        } else if (b.default) {
          return 1;
        } else {
          return 0; // otherwise respect original order
        }
      });
    return filteredDevices;
  } catch (e) {
    console.error("could not list user media devices", e);
    return "failed-to-list-devices" as const;
  }
}

function isIgnoredDevice(device: MediaDeviceInfo) {
  const IGNORED_DEVICE_LABELS = ["ZoomAudioDevice"];
  return device.label.split(" ").some((word) => IGNORED_DEVICE_LABELS.includes(word));
}

function cleanDeviceLabel(label: string) {
  return label.replace(/\s*\([^()]*:[^()]*\)\s*/, "");
}

function findDefaultDevice(devices: MediaDeviceInfo[]) {
  return devices.find((device) => {
    const hasChromeDefaultDeviceId = device.deviceId === "default";
    const hasDefaultLabel = device.label.toLowerCase().includes("default");
    if (!hasChromeDefaultDeviceId && !hasDefaultLabel) {
      return false;
    }
    const duplicate = devices.some((other) => other.groupId === device.groupId && other !== device);
    return !!duplicate;
  });
}
